import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { request } from '../../api/client';
import { cn } from '../../ui';
import { formatIst } from '../../lib/time';
import { LogValue, parseMaybeJson } from './logValueView';

export interface SmsLogRow {
  id: string;
  createdAt: string;
  adminName: string;
  mobile: string;
  templateName: string;
  templateType: string;
  messageText: string | null;
  description: string | null;
  status: string;
  httpCode: number | null;
  apiResponse?: string | null;
  payloadSent?: string | null;
}

export const SMS_STATUS_BADGE: Record<string, string> = {
  SENT: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  FAILED: 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
  SKIPPED: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  STUB: 'bg-surface-2 text-muted',
};

function Readable({ value }: { value: string | null | undefined }) {
  if (!value) return <span className="text-muted italic">—</span>;
  return <div className="rounded-control border border-line bg-surface-2/40 p-3"><LogValue value={parseMaybeJson(value)} /></div>;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-2xs uppercase tracking-widest text-muted">{label}</div>
      <div className="mt-0.5 text-sm text-content">{children}</div>
    </div>
  );
}

export function SmsLogDetailDrawer({ log, onClose }: { log: SmsLogRow; onClose: () => void }) {
  // List omits the heavy raw fields; fetch the full row on open.
  const { data: detail } = useQuery({ queryKey: ['sms-log', log.id], queryFn: () => request<SmsLogRow>({ url: `/sms-logs/${log.id}` }) });

  return createPortal(
    <>
      <div className="fixed inset-0 z-[1000] bg-black/30" onClick={onClose} aria-hidden />
      <aside className="fixed bottom-0 right-0 top-0 z-[1001] flex w-full flex-col border-l border-line bg-surface shadow-2xl sm:w-[480px] lg:w-[560px]" role="dialog" aria-modal="true">
        <div className="sticky top-0 z-10 flex items-start justify-between border-b border-line bg-surface px-5 py-4">
          <div className="min-w-0">
            <div className="text-2xs uppercase tracking-widest text-muted">WhatsApp send #{log.id}</div>
            <h2 className="mt-0.5 truncate type-section-title text-content">{log.templateName || 'message'}</h2>
          </div>
          <button onClick={onClose} className="-m-1.5 rounded p-1.5 text-muted hover:bg-surface-2" aria-label="Close"><X className="size-[18px]" /></button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4">
          <section>
            <h3 className="mb-2 text-2xs uppercase tracking-widest text-muted">Summary</h3>
            <div className="grid grid-cols-2 gap-3 rounded-control border border-line p-3">
              <Field label="When">{formatIst(log.createdAt)}</Field>
              <Field label="Status"><span className={cn('inline-block rounded px-2 py-0.5 type-caption', SMS_STATUS_BADGE[log.status] ?? 'bg-surface-2 text-content')}>{log.status || '—'}</span></Field>
              <Field label="To">{log.mobile || '—'}</Field>
              <Field label="HTTP code">{log.httpCode ?? '—'}</Field>
              <Field label="Template">{log.templateName || '—'}</Field>
              <Field label="Type">{log.templateType || '—'}</Field>
              <Field label="Triggered by">{log.adminName || 'system'}</Field>
              <div className="col-span-2"><Field label="Description">{log.description || '—'}</Field></div>
              <div className="col-span-2"><Field label="Message / params">{log.messageText || '—'}</Field></div>
            </div>
          </section>

          <section>
            <h3 className="mb-2 text-2xs uppercase tracking-widest text-muted">Payload sent</h3>
            <Readable value={detail?.payloadSent ?? null} />
          </section>
          <section>
            <h3 className="mb-2 text-2xs uppercase tracking-widest text-muted">API response</h3>
            <Readable value={detail?.apiResponse ?? null} />
          </section>
        </div>
      </aside>
    </>,
    document.body,
  );
}
